/**
 * Design tokens for inline styles and styled-components.
 *
 * `ws.*` are CSS variable references: use them for anything rendered in the DOM,
 * so light and dark themes both work. Canvas, Chart.js, MapKit and server-side
 * rendering cannot read CSS variables: use `wsHex` there, or `readToken()` when
 * the value must follow the active theme.
 */
export const ws = {
  bg: "var(--bg)",
  fg: "var(--fg)",
  muted: "var(--muted)",
  soft: "var(--soft)",
  accent: "var(--accent)",
  accentText: "var(--accent-text)",
  accentDeep: "var(--accent-deep)",
  tint: "var(--tint)",
  wash: "var(--wash)",
  card: "var(--card)",
  field: "var(--field)",
  line: "var(--line)",
  lineStrong: "var(--line-strong)",
  hover: "var(--hover)",
  glass: "var(--glass)",
  glassLine: "var(--glass-line)",
  night: "var(--night)",
  night2: "var(--night-2)",
  speed: "var(--speed)",
  distance: "var(--distance)",
  duration: "var(--duration)",
  live: "var(--live)",
  liveBg: "var(--live-bg)",
  levelBg: "var(--level-bg)",
  levelText: "var(--level-text)",
  danger: "var(--danger)",
  dangerBg: "var(--danger-bg)",
  success: "var(--success)",
  successBg: "var(--success-bg)",
  warning: "var(--warning)",
  warningBg: "var(--warning-bg)",
  overlay: "var(--overlay)",
  onAccent: "var(--on-accent)",
  shadowCard: "var(--shadow-card)",
  shadowMenu: "var(--shadow-menu)",
  shadowSoft: "var(--shadow-soft)",
  shadowTab: "var(--shadow-tab)",
  radiusXs: "var(--radius-xs)",
  radiusSm: "var(--radius-sm)",
  radiusMd: "var(--radius-md)",
  radiusLg: "var(--radius-lg)",
  radiusXl: "var(--radius-xl)",
  radiusPill: "var(--radius-pill)",
  fontDisplay: "var(--font-display)",
  fontBody: "var(--font-body)",
  ease: "var(--ease)"
} as const;

/** Light-theme values, for places that need a literal colour. */
export const wsHex = {
  bg: "#ffffff",
  fg: "#131313",
  muted: "#5f6366",
  soft: "#8a8e91",
  accent: "#0385a8",
  accentText: "#0b6991",
  accentDeep: "#035d77",
  tint: "#d6f7ff",
  wash: "#f5f8fa",
  card: "#ffffff",
  field: "#edeff7",
  line: "#e5e7e8",
  lineStrong: "#c9cdcf",
  night: "#00222c",
  night2: "#0b1417",
  speed: "#ff5101",
  distance: "#0b69f6",
  duration: "#0ab9aa",
  live: "#d0021b",
  danger: "#c4001a",
  success: "#006b37",
  warning: "#9a5b00"
} as const;

export type WsHexToken = keyof typeof wsHex;

const toCssName = (token: string) => `--${token.replace(/[A-Z0-9]/g, (c) => `-${c.toLowerCase()}`)}`;

/** Resolves a token to its current computed value, so canvas drawings can follow the theme. */
export function readToken(token: WsHexToken): string {
  if (typeof window === "undefined" || !window.getComputedStyle) {
    return wsHex[token];
  }
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(toCssName(token)).trim();
  return value || wsHex[token];
}
